import * as React from "react";
import MaskedInput from "react-text-mask";
import { get, isObservable, isObservableProp } from "mobx";
import { ensureObservableProperty } from "@frui.ts/helpers";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent } from "@frui.ts/views";
import { bound } from "@frui.ts/helpers";
import { Observer } from "mobx-react-lite";
import type { FormControlProps } from "react-bootstrap";
import { Form, InputGroup } from "react-bootstrap";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import type { InputProps } from "@frui.ts/bootstrap/dist/controls/input";
import { getValidationMessage } from "@frui.ts/validation";
import { formatIBAN, isIBANFormat } from "@edaz/utils/bankAccount";

export interface IbanInputWithAppendProps {
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: boolean;
  inputType?: string;
  onInvalidValuePasted?: (pastedValue: string, event: any) => void;
}

export function ibanMask(rawValue: string) {
  const maxIBANSize = 34;
  const spaceAfterFourCharacters = 4;
  const maskMaxSize = maxIBANSize + Math.floor(maxIBANSize / spaceAfterFourCharacters);

  const spaceCount = Math.floor(rawValue.length / spaceAfterFourCharacters) + 1;
  const maskSize = rawValue.length < maskMaxSize ? rawValue.length + spaceCount : maskMaxSize;

  return Array.from(Array(maskSize).keys()).map(key => {
    if ((key + 1) % (spaceAfterFourCharacters + 1) === 0) {
      return " ";
    } else if (key < 2) {
      return /[A-Z]|[a-z]/;
    }

    return /[0-9]|[A-Z]|[a-z]/;
  });
}

const UNBREAKABLE_SPACE = " ";

export default class IbanInput<TTarget> extends BindingComponent<
  TTarget,
  IbanInputWithAppendProps & FormControlProps & InputProps & CommonInputProps & IBindingProps<TTarget>
> {
  render() {
    return <Observer render={this.renderInner} />;
  }

  protected get value() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string }; // the cast is a TS compiler workaround
    if (!target) {
      // tslint:disable-next-line: no-console
      console.warn("'target' prop has not been set");
      return undefined;
    }
    if (!property) {
      throw new Error("'property' prop has not been set");
    }
    if (!isObservable(target) || !isObservableProp(target, property)) {
      ensureObservableProperty(target, property, target[property]);
    }

    return formatIBAN(get(target, property));
  }

  @bound protected renderInner() {
    const { noValidation, errorMessage, append, prepend, placeholder, disabled, inputType, isInvalid, autoComplete } =
      this.inheritedProps;
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    const validationError = (noValidation !== true && errorMessage) || getValidationMessage(target, property);

    return (
      <InputGroup>
        {prepend && (
          <InputGroup.Prepend>
            <InputGroup.Text>{prepend}</InputGroup.Text>
          </InputGroup.Prepend>
        )}
        <MaskedInput
          mask={ibanMask}
          className={`form-control ${validationError || isInvalid ? "is-invalid" : ""} ${this.props.className}`}
          guide={true}
          keepCharPositions={true}
          placeholderChar={UNBREAKABLE_SPACE}
          value={this.value}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          autoFocus={this.props.autoFocus}
          onChange={this.handleValueChanged}
          onPaste={this.onPaste}
          placeholder={placeholder}
          autoComplete={autoComplete ? undefined : "new-password"}
          disabled={disabled}
          type={inputType}
        />
        {append && (
          <InputGroup.Append>
            <InputGroup.Text>{append}</InputGroup.Text>
          </InputGroup.Append>
        )}
        {validationError && <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>}
      </InputGroup>
    );
  }

  @bound protected handleValueChanged(e: React.FormEvent<any>) {
    const target = e.target as HTMLInputElement;
    let newValue = target.value;
    newValue = newValue.replace(/\s*$/, "").replaceAll(" ", "");

    this.setValue(newValue.toUpperCase());
  }

  @bound
  protected onPaste(event: any) {
    const pastedValue = event.clipboardData.getData("text").trim();
    if (pastedValue && !isIBANFormat(pastedValue) && this.props.onInvalidValuePasted) {
      // If the onInvalidValuePasted handler sets the input data, it needs to prevent default on the paste event
      this.props.onInvalidValuePasted(pastedValue.replaceAll(" ", ""), event);
    }
  }
}
